import React, { useEffect, useState } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Shield, Heart, DollarSign, FileText, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

export default function Invite() {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthAndToken();
  }, []);

  const checkAuthAndToken = async () => {
    // Extract token from URL
    const urlParams = new URLSearchParams(window.location.search);
    const inviteToken = urlParams.get('token');
    
    if (!inviteToken) {
      // No token, redirect to welcome
      window.location.href = '/';
      return;
    }

    setToken(inviteToken);

    // Check if already authenticated
    try {
      const isAuth = await api.auth.isAuthenticated();
      if (isAuth) {
        // Already logged in, go directly to InviteCodeEntry
        window.location.href = `/InviteCodeEntry?token=${inviteToken}`;
        return;
      }
    } catch (err) {
      // Not authenticated, stay on this page
    }

    setLoading(false);
  };

  const handleContinue = () => {
    // Redirect to Base44 auth, then return to InviteCodeEntry with token
    api.auth.redirectToLogin(`/InviteCodeEntry?token=${token}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-bold text-slate-900">BenefitsPro</span>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-20 h-20 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center mx-auto mb-6">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            You've Been Invited!
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Your employer has invited you to join their employee benefits program. 
            Get ready to explore your benefits and maximize your tax savings.
          </p>
        </motion.div>

        {/* Benefits Preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid md:grid-cols-3 gap-6 mb-12"
        >
          <Card>
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <Heart className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="font-semibold mb-2">Choose Your Benefits</h3>
              <p className="text-sm text-slate-600">
                Browse and select from a variety of health and wellness benefits
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-3">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold mb-2">Save on Taxes</h3>
              <p className="text-sm text-slate-600">
                Use pre-tax dollars with Section 125 to maximize your savings
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mx-auto mb-3">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold mb-2">Easy Enrollment</h3>
              <p className="text-sm text-slate-600">
                Complete your enrollment in minutes with our guided process
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* CTA Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-gradient-to-br from-emerald-500 to-teal-500 border-0">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-3xl text-white">Ready to Get Started?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-white">
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  Next Steps:
                </h3>
                <ol className="space-y-2 text-white/90">
                  <li className="flex items-start gap-2">
                    <span className="font-bold">1.</span>
                    <span>Create your secure account (or sign in if you already have one)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold">2.</span>
                    <span>Complete your employee profile</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold">3.</span>
                    <span>Browse available benefits and enroll in your selections</span>
                  </li>
                </ol>
              </div>

              <Button
                onClick={handleContinue}
                size="lg"
                className="w-full bg-white text-emerald-600 hover:bg-slate-50 text-lg py-6"
              >
                Continue to Registration
              </Button>

              <p className="text-center text-white/80 text-sm">
                This will take you to a secure login page where you can create your account
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-400">Â© 2025 BenefitsPro. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}